import { useCallback, useEffect, useRef, useState } from "react";
import { isAxiosError } from "axios";
import { Loader2, MailCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OtpSlots } from "@/components/auth/OtpSlots";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

interface OtpSignInProps {
  defaultEmail?: string;
  onSuccess: () => void;
}

interface OtpRequestState {
  requestId: string;
  nonce: string;
  length: number;
}

const CLAIM_POLL_MS = 2500;

/**
 * Passwordless sign-in lane: email step, then code step. The code step both
 * accepts the emailed code in slot inputs and polls /auth/otp/claim so that
 * clicking "Approve sign-in" in the email signs THIS tab in automatically.
 */
export function OtpSignIn({ defaultEmail = "", onSuccess }: OtpSignInProps) {
  const { adoptSession } = useAuth();
  const [email, setEmail] = useState(defaultEmail);
  const [otpRequest, setOtpRequest] = useState<OtpRequestState | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // Once a session lands (via verify OR claim), every other in-flight call
  // must become a no-op — the loser of the race returns 401 by design.
  const doneRef = useRef(false);

  const finishSignIn = useCallback(
    (data: { access_token: string; user: Parameters<typeof adoptSession>[1] }) => {
      if (doneRef.current) return;
      doneRef.current = true;
      adoptSession(data.access_token, data.user);
      onSuccess();
    },
    [adoptSession, onSuccess]
  );

  const requestCode = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      const response = await api.post("/auth/otp/request", { email });
      const { request_id, nonce, length } = response.data;
      setCode("");
      setOtpRequest({ requestId: request_id, nonce, length: Math.min(Math.max(length, 6), 10) });
    } catch (err) {
      setError(
        isAxiosError(err) && err.response?.status === 429
          ? "Too many requests. Wait a minute and try again."
          : (isAxiosError(err) && err.response?.data?.message) ||
              "Could not send the code. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const verifyCode = useCallback(
    async (fullCode: string) => {
      if (!otpRequest || doneRef.current) return;
      setError(null);
      setIsLoading(true);
      try {
        const response = await api.post(
          "/auth/otp/verify",
          { request_id: otpRequest.requestId, nonce: otpRequest.nonce, code: fullCode },
          { withCredentials: true }
        );
        finishSignIn(response.data);
      } catch (err) {
        if (!doneRef.current) {
          setCode("");
          setError(
            (isAxiosError(err) && err.response?.data?.message) ||
              "That code is invalid or has expired."
          );
        }
      } finally {
        setIsLoading(false);
      }
    },
    [otpRequest, finishSignIn]
  );

  // Auto-submit when all slots are filled
  useEffect(() => {
    if (otpRequest && code.length === otpRequest.length && !isLoading) {
      verifyCode(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, otpRequest]);

  // Poll for email-button approval while the code step is showing
  useEffect(() => {
    if (!otpRequest) return;
    let inFlight = false;
    const interval = setInterval(async () => {
      if (inFlight || doneRef.current) return;
      inFlight = true;
      try {
        const response = await api.post(
          "/auth/otp/claim",
          { request_id: otpRequest.requestId, nonce: otpRequest.nonce },
          { withCredentials: true }
        );
        if (response.data?.access_token) {
          finishSignIn(response.data);
        }
      } catch {
        // pending requests never error; an error here means the request was
        // consumed elsewhere (e.g. code verify won) or expired — the expiry
        // path surfaces through verify, so stay quiet
      } finally {
        inFlight = false;
      }
    }, CLAIM_POLL_MS);
    return () => clearInterval(interval);
  }, [otpRequest, finishSignIn]);

  if (!otpRequest) {
    return (
      <form onSubmit={requestCode} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="otp-email">
            Email <span className="text-destructive">*</span>
          </Label>
          <Input
            id="otp-email"
            className="min-h-[44px]"
            type="email"
            inputMode="email"
            placeholder="you@example.com"
            autoComplete="email"
            autoFocus
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        {error && (
          <p role="alert" className="text-sm text-red-500 animate-shake">
            {error}
          </p>
        )}
        <Button type="submit" className="w-full min-h-[44px]" disabled={isLoading || !email}>
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Email me a sign-in code
        </Button>
      </form>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
        <MailCheck className="h-4 w-4 text-primary" />
        <span>
          Code sent to <span className="font-medium text-foreground">{email}</span>
        </span>
      </div>

      <OtpSlots
        length={otpRequest.length}
        value={code}
        onChange={(v) => {
          setError(null);
          setCode(v);
        }}
        disabled={isLoading}
        error={!!error}
      />

      {error ? (
        <p role="alert" className="text-center text-sm text-red-500 animate-shake">
          {error}
        </p>
      ) : (
        <p className="text-center text-xs text-muted-foreground">
          <Loader2 className="mr-1.5 inline h-3 w-3 animate-spin align-[-2px]" />
          Or click <span className="font-medium">Approve sign-in</span> in the email — this tab
          will sign in by itself
        </p>
      )}

      <div className="flex items-center justify-center gap-4 text-sm">
        <button
          type="button"
          onClick={() => requestCode()}
          disabled={isLoading}
          className="text-accent hover:underline disabled:opacity-50"
        >
          Resend code
        </button>
        <span className="text-muted-foreground">·</span>
        <button
          type="button"
          onClick={() => {
            setOtpRequest(null);
            setCode("");
            setError(null);
          }}
          className="text-accent hover:underline"
        >
          Use a different email
        </button>
      </div>
    </div>
  );
}
